

















































































































import { API_ENDPOINT } from '@/env';
import ImageCanvas from '@/features/ui/image-canvas/ImageCanvas.global.vue';
import ContainerMixin from '@/features/core/components/mixins/container';
import { AddToastMessageParams } from '@/features/core/store/toast';
import { KEYCLOAK } from '@/features/core/container';
import query from '@/features/app-manager/views/tree-node/ground-plan/view.gql';
import placeGroundPlanSpotMutation from '@/features/app-manager/views/tree-node/ground-plan/place-ground-plan-spot.gql';
import setGroundPlanFileMutation from '@/features/app-manager/views/tree-node/ground-plan/set-ground-plan-file.gql';
import GroundPlanSpotPlacement from '@/features/app-manager/views/tree-node/ground-plan/GroundPlanSpotPlacement.vue';
import uploadTreeNodeFileMutation from '@/features/app-manager/components/tree-node-files-control/upload-tree-node-file.gql';
import deleteTreeNodeFileMutation from '@/features/app-manager/components/tree-node-files-control/delete-tree-node-file.gql';
import CanvasSkeleton from './CanvasSkeleton.vue';
import { defineComponent } from '@vue/composition-api';
import FormMessage from '@/features/ui/form/FormMessage.global.vue';
import { TreeNodeType } from '@/types/iot-portal';
import getTreeNodesUnderPropertyQuery from '@/features/app-heating-system/views/tree-node/get-tree-nodes-under-property.gql';

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

interface SchematicTabData {
  editable: boolean;
  imageSrc: string | undefined;
  fileLabel: string;
  placements: Array<{ key: string; value: any; position: any }>;
  sensors: any[];
  imageFileId: string | undefined;
  treeNodes: any;
  imageLoading: boolean;
  queryError: boolean;
  lastFetchedImageId: string | undefined;
  placementLoading: boolean;
  draggingSensorId: string | null;
  treeNodeId: string | null;
}

export default defineComponent<any, SchematicTabData, any, any>({
  name: 'SchematicTab',
  components: {
    ImageCanvas,
    GroundPlanSpotPlacement,
    CanvasSkeleton,
    FormMessage,
  },
  mixins: [ContainerMixin],

  apollo: {
    treeNodes: {
      query,
      fetchPolicy: 'cache-and-network',
      variables() {
        return { treeNodeId: this.treeNodeId };
      },
      skip() {
        return !this.treeNodeId;
      },
      loadingKey: 'imageLoading',
      async result(this: any, { data }: { data: any }) {
        this.queryError = false; // reset error on success
        if (data && data.treeNodes && data.treeNodes.first && data.treeNodes.first.groundPlanFile) {
          const groundPlanFile = data.treeNodes.first.groundPlanFile;
          this.imageFileId = groundPlanFile?.id;
          const files = data.treeNodes.first.files.items || [];
          const fileObj = files.find((f: any) => f.id === groundPlanFile?.id);
          this.fileLabel = fileObj?.label || '';
          // Map placements for the canvas
          const allPlacements: any[] = groundPlanFile?.spotPlacements || [];
          this.placements = allPlacements.map((value: any) => ({
            key: value.id,
            value,
            position: 'position' in value ? value.position : undefined,
          }));
          this.sensors = allPlacements.filter((p: any) => !('position' in p));
          if (this.imageFileId && this.imageFileId !== this.lastFetchedImageId) {
            this.lastFetchedImageId = this.imageFileId;
            await this.fetchImageById(this.imageFileId as string);
          }
        } else {
          this.imageFileId = undefined;
          this.fileLabel = '';
          this.placements = [];
          this.sensors = [];
          this.imageSrc = undefined;
          this.imageLoading = false;
          this.queryError = false;
        }
      },
      error(error) {
        this.queryError = true;
        this.imageLoading = false;
        console.error('Ground plan query failed:', error);
      },
    },
  },
  props: {
    mainTreeNodeId: {
      type: String,
      required: true,
    },
  },
  data(): SchematicTabData {
    return {
      editable: false,
      imageSrc: undefined,
      fileLabel: '',
      placements: [],
      sensors: [],
      imageFileId: undefined,
      treeNodes: undefined,
      imageLoading: false,
      queryError: false,
      lastFetchedImageId: undefined,
      placementLoading: false,
      draggingSensorId: null,
      treeNodeId: null,
    };
  },
  computed: {
    isLoading() {
      return this.imageLoading || this.$apollo.queries.treeNodes.loading;
    },
  },
  watch: {
    mainTreeNodeId: {
      immediate: true,
      async handler(newVal) {
        this.imageLoading = true;
        if (this.mainTreeNodeId) {
          await this.fetchTreeNodesUnderProperty(this.mainTreeNodeId);
        }

        const preferredNode = this.findPreferredStationNode();
        this.treeNodeId = preferredNode ? preferredNode.id : null;
        this.imageLoading = !!this.treeNodeId;
      },
    },
  },
  methods: {
    openFileDialog(): void {
      if (!this.imageSrc) {
        (this.$refs.fileInput as HTMLInputElement).click();
      }
    },
    async handleImageSelection(file: File): Promise<void> {
      if (file) {
        if (!/^image\/(jpeg|png|svg(\+xml)?)$/i.test(file.type)) {
          this.$store.dispatch('ADD_TOAST_MESSAGES', {
            messages: [{ text: 'Nur Bilddateien (JPG, PNG, SVG) sind erlaubt.' }],
            duration: 10000,
          } as AddToastMessageParams);
          return;
        }
        if (file.size > MAX_IMAGE_SIZE) {
          this.$store.dispatch('ADD_TOAST_MESSAGES', {
            messages: [{ text: 'Die Datei ist zu groß. Bitte wählen Sie eine Datei mit maximal 10MB.' }],
            duration: 10000,
          } as AddToastMessageParams);
          return;
        }
        await this.uploadImage(file as File);
      }
    },
    handleFileChange(event: Event): void {
      const target = event.target as HTMLInputElement;
      if (!(target instanceof HTMLInputElement)) return;
      const file = target.files && target.files[0];
      if (file) {
        this.handleImageSelection(file as File);
      }
      target.value = '';
    },
    handleDrop(event: DragEvent): void {
      if (this.imageLoading || !this.treeNodeId) return;
      if (this.imageSrc) event.preventDefault();
      const file: File | undefined =
        event.dataTransfer && event.dataTransfer.files[0] ? (event.dataTransfer.files[0] as File) : undefined;
      this.handleImageSelection(file as File);
    },
    async uploadImage(file: File): Promise<void> {
      try {
        if (!file) return;

        this.imageLoading = true;
        const { data } = await this.$apollo.mutate({
          mutation: uploadTreeNodeFileMutation,
          variables: {
            input: {
              treeNodeId: this.treeNodeId,
              file,
              label: file.name,
            },
          },
        });
        const fileId = data.uploadTreeNodeFile.treeNodeFile.id;
        await this.retrySetGroundPlanFile(this.setGroundPlanFile.bind(this), fileId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Image upload failed', e);
        this.$store.dispatch('ADD_TOAST_MESSAGES', {
          messages: [{ text: 'Bild konnte nicht hochgeladen werden.' }],
          duration: 10000,
        } as AddToastMessageParams);
      } finally {
        this.imageLoading = false;
      }
    },
    async removeImage(): Promise<void> {
      if (!window.confirm('Möchten Sie das Bild und alle Platzierungen wirklich entfernen?')) {
        return;
      }
      if (!this.imageFileId) return;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: deleteTreeNodeFileMutation,
          variables: {
            input: {
              id: this.imageFileId,
            },
          },
        });
        if (!data) throw new Error('Delete failed');
        this.imageSrc = undefined;
        this.fileLabel = '';
        this.imageFileId = undefined;
        await (this as any).setGroundPlanFile(null);
        this.$store.dispatch('ADD_TOAST_MESSAGES', {
          messages: [{ text: 'Bild erfolgreich entfernt.' }],
        } as AddToastMessageParams);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Image delete failed', e);
        this.$store.dispatch('ADD_TOAST_MESSAGES', {
          messages: [{ text: 'Bild konnte nicht entfernt werden.' }],
          duration: 10000,
        } as AddToastMessageParams);
      }
    },
    async setGroundPlanFile(treeNodeFileId: string | null): Promise<void> {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: setGroundPlanFileMutation,
          variables: {
            input: {
              treeNodeId: this.treeNodeId,
              treeNodeFileId,
            },
          },
        });
        if (!data) {
          throw new Error('Data missing');
        }
        this.$apollo.queries.treeNodes.refetch();
        this.imageFileId = treeNodeFileId || undefined;
        if (treeNodeFileId) {
          this.$store.dispatch('ADD_TOAST_MESSAGES', {
            messages: [{ text: 'Bild erfolgreich gesetzt.' }],
          } as AddToastMessageParams);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        this.$store.dispatch('ADD_TOAST_MESSAGES', {
          messages: [{ text: 'Das Bild konnte nicht gesetzt werden.' }],
          duration: 10000,
        } as AddToastMessageParams);
        throw e;
      }
    },
    async retrySetGroundPlanFile(
      setGroundPlanFileFn: (fileId: string) => Promise<void>,
      fileId: string,
      retries = 3,
      delay = 400,
    ) {
      for (let i = 0; i < retries; i++) {
        try {
          await setGroundPlanFileFn(fileId);
          return;
        } catch (e) {
          if (
            i === retries - 1 ||
            !(e && typeof e === 'object') ||
            !String((e as any).message).includes('TreeNodeFileEntity')
          ) {
            throw e;
          }
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    },
    onSensorDragStart(event: DragEvent, row: any): void {
      (this.$refs.imageCanvas as any)?.dragStart?.(event, row.id);
    },
    findFreeSpot(
      this: { placements: Array<{ key: string; value: any; position: any }> },
      position: { x: number; y: number },
    ): { x: number; y: number } {
      const OFFSET = 0.08;
      const MARGIN = 0.05;
      const THRESHOLD = 0.03;
      let attempts = 0;
      const maxAttempts = 20;
      let newPosition = { ...position };

      const isOccupied = (pos: { x: number; y: number }): boolean => {
        return this.placements.some(
          (placement: any) =>
            placement.position &&
            Math.abs(placement.position.x - pos.x) < THRESHOLD &&
            Math.abs(placement.position.y - pos.y) < THRESHOLD,
        );
      };

      // Spiral search for a free spot
      while (isOccupied(newPosition) && attempts < maxAttempts) {
        const dynamicOffset = OFFSET * (Math.floor(attempts / 4) + 1);
        switch (attempts % 4) {
          case 0:
            newPosition.x = Math.min(newPosition.x + dynamicOffset, 1);
            break;
          case 1:
            newPosition.y = Math.min(newPosition.y + dynamicOffset, 1);
            break;
          case 2:
            newPosition.x = Math.max(newPosition.x - dynamicOffset, 0);
            break;
          case 3:
            newPosition.y = Math.max(newPosition.y - dynamicOffset, 0);
            break;
        }
        attempts++;
      }

      // Clamp to keep inside canvas with margin
      newPosition.x = Math.max(MARGIN, Math.min(newPosition.x, 1 - MARGIN));
      newPosition.y = Math.max(MARGIN, Math.min(newPosition.y, 1 - MARGIN));

      // Fallback: scan for a free spot if still occupied
      if (isOccupied(newPosition)) {
        let minDist = Infinity;
        let bestPos = { x: newPosition.x, y: newPosition.y };
        for (let x = MARGIN; x <= 1 - MARGIN; x += 0.01) {
          for (let y = MARGIN; y <= 1 - MARGIN; y += 0.01) {
            if (!isOccupied({ x, y })) {
              const dist = Math.hypot(x - position.x, y - position.y);
              if (dist < minDist) {
                minDist = dist;
                bestPos = { x, y };
              }
            }
          }
        }
        newPosition = bestPos;
      }

      return newPosition;
    },
    async onPlacementDropped(this: any, { value, position }: { value: any; position: any }): Promise<void> {
      this.placementLoading = true;
      let newPosition = { ...position };

      if (position !== undefined) {
        newPosition = this.findFreeSpot(position);
      } else {
        newPosition.x = null;
        newPosition.y = null;
      }

      try {
        if (!this.imageFileId) return;
        await this.placeSchematicSpot(value.spot.id, newPosition);
      } finally {
        this.placementLoading = false;
      }
    },
    async fetchImageById(id: string): Promise<void> {
      if (!id) {
        this.imageSrc = undefined;
        this.imageLoading = false;
        this.placements = [];
        this.sensors = [];
        return;
      }
      this.imageLoading = true;
      try {
        const endpoint = new URL(API_ENDPOINT, window.location.href);
        endpoint.username = '';
        endpoint.password = '';
        const url = new URL(`api/file/${id}`, endpoint);
        const token: string = ((this as any).container(KEYCLOAK) as { token?: string } | undefined)?.token || '';
        const response = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const reader = new FileReader();
        reader.onloadend = () => {
          if (this.imageFileId === id) {
            this.imageSrc = reader.result?.toString();
          }
          this.imageLoading = false;
        };
        reader.readAsDataURL(await response.blob());
      } catch (e) {
        // eslint-disable-next-line no-console
        this.imageLoading = false;
        this.queryError = true;
        this.imageSrc = undefined;
        this.placements = [];
        this.sensors = [];
        console.error('Failed to fetch image', e);
      }
    },
    async placeSchematicSpot(spotId: string, position: any): Promise<void> {
      if (!this.imageFileId) return;
      const input: any = {
        treeNodeFileId: this.imageFileId,
        spotId,
      };
      if (position && typeof position.x === 'number' && typeof position.y === 'number') {
        input.position = position;
      }
      try {
        const { data } = await this.$apollo.mutate({
          mutation: placeGroundPlanSpotMutation,
          variables: { input },
          update: (store: any, mutationResult: { data?: any }) => {
            const { data: spotPlacementData } = mutationResult;
            if (!spotPlacementData) return;
            const viewData = store.readQuery({
              query,
              variables: { treeNodeId: this.treeNodeId },
            });
            if (!viewData || !('groundPlanFile' in viewData.treeNodes.first)) {
              return;
            }
            interface SpotPlacement {
              id: string;
              spot: { id: string };
              position?: Record<string, unknown>;
            }
            const existingSpotPlacement = viewData.treeNodes.first.groundPlanFile?.spotPlacements.find(
              (spotPlacement: SpotPlacement) =>
                spotPlacement.spot.id === spotPlacementData.placeGroundPlanSpot.placement.spot.id,
            );
            if (!existingSpotPlacement) {
              viewData?.treeNodes.first.groundPlanFile?.spotPlacements.push(
                spotPlacementData.placeGroundPlanSpot.placement,
              );
            }
            store.writeQuery({ query, data: viewData });
          },
        });
        if (!data) {
          this.$store.dispatch('ADD_TOAST_MESSAGES', {
            messages: [{ text: 'Die Position konnte nicht gespeichert werden.' }],
            duration: 10000,
          } as AddToastMessageParams);
          return;
        }
      } catch (e) {
        // eslint-disable-next-line no-console -- dont swallow errors
        console.error(e);
        this.$store.dispatch('ADD_TOAST_MESSAGES', {
          messages: [{ text: 'Die Position konnte nicht gespeichert werden.' }],
          duration: 10000,
        } as AddToastMessageParams);
      }
    },
    async confirmRemoveImage(hide: () => void): Promise<void> {
      hide();
      if (!this.imageFileId) return;
      this.imageLoading = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: deleteTreeNodeFileMutation,
          variables: {
            input: {
              id: this.imageFileId,
            },
          },
        });
        if (!data) throw new Error('Delete failed');
        this.imageSrc = undefined;
        this.fileLabel = '';
        this.imageFileId = undefined;
        await (this as any).setGroundPlanFile(null);
        this.$store.dispatch('ADD_TOAST_MESSAGES', {
          messages: [{ text: 'Bild erfolgreich entfernt.' }],
        } as AddToastMessageParams);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Image delete failed', e);
        this.$store.dispatch('ADD_TOAST_MESSAGES', {
          messages: [{ text: 'Bild konnte nicht entfernt werden.' }],
          duration: 10000,
        } as AddToastMessageParams);
      } finally {
        this.imageLoading = false;
      }
    },
    findSubdivisionTreeNode(roomName: string): Record<string, unknown> | null {
      if (
        this.treeNodesUnderProperty &&
        this.treeNodesUnderProperty.children &&
        Array.isArray(this.treeNodesUnderProperty.children.items)
      ) {
        const normalize = (str: string): string => str.toLowerCase().replace(/[\s-]+/g, '');
        const normalizedRoomName = normalize(roomName);

        return (
          this.treeNodesUnderProperty.children.items.find(
            (item: { __typename: string; name?: string }) =>
              typeof item.name === 'string' && normalize(item.name).includes(normalizedRoomName),
          ) || null
        );
      }
      return null;
    },
    findPreferredStationNode(): Record<string, unknown> | null {
      return this.findSubdivisionTreeNode('HA Station') || this.findSubdivisionTreeNode('Heizungsraum') || null;
    },
    async fetchTreeNodesUnderProperty(propertyId: string) {
      try {
        const { data } = await this.$apollo.query({
          query: getTreeNodesUnderPropertyQuery,
          variables: {
            id: propertyId,
            types: [TreeNodeType.PropertySubdivision],
          },
        });
        this.treeNodesUnderProperty =
          data.treeNodes.items && data.treeNodes.items[0] && data.treeNodes.items[0].children
            ? data.treeNodes.items[0]
            : [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch tree nodes under property:', error);
        this.treeNodesUnderProperty = [];
      }
    },
  },
});
